export const PROFILE_PICTURE_MAX_DIMENSION = 640;

export function validateCustomerPicture(file) {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    throw new Error("Choose a JPEG, PNG or WebP picture.");
  }
  if (!file.size || file.size > 20 * 1024 * 1024) {
    throw new Error("Choose a non-empty picture no larger than 20 MB.");
  }
}

export async function optimizeCustomerPicture(file, crop = null) {
  validateCustomerPicture(file);
  const url = URL.createObjectURL(file);
  const image = new Image();
  try {
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error("Could not read this picture. Choose a valid image."));
      image.src = url;
    });
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!width || !height || width * height > 25_000_000) {
      throw new Error("Choose a picture no larger than 25 megapixels.");
    }
    const area = crop || { x: 0, y: 0, width, height };
    if (![area.x, area.y, area.width, area.height].every(Number.isFinite)
      || area.x < 0 || area.y < 0 || area.width <= 0 || area.height <= 0
      || area.x + area.width > width || area.y + area.height > height) {
      throw new Error("Choose a valid crop inside the picture.");
    }
    const scale = Math.min(1, PROFILE_PICTURE_MAX_DIMENSION / Math.max(area.width, area.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(area.width * scale));
    canvas.height = Math.max(1, Math.round(area.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Image processing is unavailable in this browser.");
    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, canvas.width, canvas.height);
    if (crop) context.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
    else context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.8));
    if (!blob || !blob.size || blob.size > 5 * 1024 * 1024) {
      throw new Error("Could not compress this picture. Choose a smaller image.");
    }
    return new File([blob], "profile-picture.jpg", { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(url);
  }
}
